import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { GLOBAL_SEARCH_LISTBOX_ID, GLOBAL_SEARCH_MIN_QUERY_LENGTH } from '../../helper/constants';
import GlobalSearchBox from './GlobalSearchBox';
import GlobalSearchResults from './GlobalSearchResults';

const useStyles = makeStyles(() => ({
    root: {
        position: 'relative'
    }
}));

function GlobalSearchContainer({ sidebarOpen }) {
    const classes = useStyles();
    const history = useHistory();
    // Default true keeps the control visible while the flag cache warms up so the
    // sidebar does not flash empty on first render. The flag must explicitly
    // resolve to false to hide the whole block.
    const autocompleteEnabled = useFeatureFlag(FEATURE_FLAGS.GLOBAL_SEARCH_AUTOCOMPLETE, true);
    const anchorRef = useRef(null);
    const [query, setQuery] = useState('');
    const [selectedModule, setSelectedModule] = useState('All');
    const [overlayForcedClosed, setOverlayForcedClosed] = useState(false);

    const handleQueryChange = useCallback((value) => {
        setQuery(value);
        setOverlayForcedClosed(false);
    }, []);

    const handleModuleChange = useCallback((module) => {
        setSelectedModule(module);
    }, []);

    const handleClose = useCallback(() => {
        setOverlayForcedClosed(true);
    }, []);

    const handleShowMore = useCallback((finalQuery, finalModule) => {
        const params = new URLSearchParams();
        params.set('q', finalQuery);
        params.set('module', finalModule);
        history.push(`/tools/global-search/full?${params.toString()}`);
    }, [history]);

    const trimmed = query.trim();
    const shouldShowOverlay = !overlayForcedClosed && trimmed.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH;

    if (sidebarOpen === false || !autocompleteEnabled) {
        return null;
    }

    return (
        <div ref={anchorRef} className={classes.root}>
            <GlobalSearchBox
                sidebarOpen={sidebarOpen}
                query={query}
                onQueryChange={handleQueryChange}
                selectedModule={selectedModule}
                onModuleChange={handleModuleChange}
                searchAriaControls={GLOBAL_SEARCH_LISTBOX_ID}
                searchAriaExpanded={shouldShowOverlay}
            />
            {shouldShowOverlay && (
                <GlobalSearchResults
                    query={query}
                    moduleFilter={selectedModule}
                    onClose={handleClose}
                    onShowMore={handleShowMore}
                    listboxId={GLOBAL_SEARCH_LISTBOX_ID}
                    anchorEl={anchorRef.current}
                />
            )}
        </div>
    );
}

GlobalSearchContainer.propTypes = {
    sidebarOpen: PropTypes.bool
};

GlobalSearchContainer.defaultProps = {
    sidebarOpen: true
};

export default GlobalSearchContainer;
